import React, { useEffect, useState } from 'react';
import { Flex, Box, Text } from '@chakra-ui/layout';
import { Button } from '@chakra-ui/button';
import { HStack, useMediaQuery, VStack } from '@chakra-ui/react';
import { ObjectBase, ObjectImage, ObjectPrice, ObjectResponse, ObjectText } from '../../types/ObjectTypes';
import { ImageGallery } from '../common/ImageGallery/ImageGallery';
import { ObjectPriceInformation } from './ObjectPriceInformation';
import { Bookmark } from '../Objects/ListItem/Bookmark';
import { ObjectHighlightedBaseData } from './ObjectHighlightedBaseData';
import { ExtendedDetailsRoute } from '../../types/RoutesTypes';
import { ObjectParams } from '../../types/ParamsTypes';
import { Link as ScrollLink } from 'react-scroll';
import { getAxiosWithConfig } from '../../services/Http/HttpService';
import { AxiosResponse } from 'axios';
import { BookingCustomer } from '../../types/BookingTypes';
import { getInitialCustomerParams } from '../../services/Booking/BookingService';
import { APP_VIEW, getAppView } from '../../services/VersionManagement/VersionManagementService';

interface ObjectDetailsMainInformationProps {
  imagesData: ObjectImage[];
  baseData: ObjectBase;
  textsData: Array<ObjectText>;
  price?: ObjectPrice;
  isLoading: Boolean;
  handleBookmarkChange: (value: ExtendedDetailsRoute, action?: string | undefined) => void;
  bookmarks: ExtendedDetailsRoute[];
  bookmarkParams?: ExtendedDetailsRoute;
  bookingRouteParams: ObjectParams;
  handleBookingCalendarModal: (value: boolean) => void;
}

export const ObjectMainInformation = ({
  baseData,
  imagesData,
  isLoading,
  price,
  handleBookmarkChange,
  bookmarks,
  bookmarkParams,
  bookingRouteParams,
  handleBookingCalendarModal,
}: ObjectDetailsMainInformationProps): JSX.Element => {
  const [isTabletOrMobile] = useMediaQuery('(max-width: 767px)');
  const [bookingButtonText, setBookingButtonText] = useState('');
  const [appView] = useState<APP_VIEW>(getAppView());

  useEffect(() => {
    if (baseData.OnlineBooking && baseData.OnlineOffer && !baseData.OnlineRequest) {
      setBookingButtonText('Weiter zur Buchung / zum Angebot');
    }
    if (baseData.OnlineBooking && baseData.OnlineOffer && baseData.OnlineRequest) {
      setBookingButtonText('Weiter zur Buchung / zur Anfrage');
    }
    if (baseData.OnlineBooking && !baseData.OnlineOffer) {
      setBookingButtonText('Weiter zur Buchung');
    }
    if (!baseData.OnlineBooking && baseData.OnlineOffer && !baseData.OnlineRequest) {
      setBookingButtonText('Weiter zum Angebot');
    }
    if (!baseData.OnlineBooking && baseData.OnlineOffer && baseData.OnlineRequest) {
      setBookingButtonText('Weiter zur Anfrage');
    }
    if (!baseData.OnlineBooking && !baseData.OnlineOffer) {
      setBookingButtonText('Nur telefonisch Buchbar');
    }
  }, [setBookingButtonText]);


  return (

    <>
      {!isLoading && (
        <VStack
          pl={{ sm: 0, md: 4, lg: 4, xl: 4 }}
          pr={{ sm: 0, md: 4, lg: 4, xl: 4 }}
          pt={4}
          pb={4}
          borderRadius={{ sm: 0, md: 4, lg: 4, xl: 4 }}
          backgroundColor={'white'}
          w="full"
          width={'100%'}
          alignItems={'baseline'}>
          <HStack
            width={'100%'}
            alignItems={'center'}
            pr={{ base: 4, md: 0 }}
            pl={{ base: 4, md: 0 }}>
            <Text
              fontWeight={'semibold'}
              width={'100%'}
              textAlign={'left'}
              fontSize={{ base: 24, md: '3xl' }}>
              {baseData.Name}
            </Text>
            <Box>
              {appView !== APP_VIEW.MIN &&
                <Bookmark
                  size={'4xl'}
                  bookmarkContext={'objectDetailsView'}
                  objectUniqueId={baseData.uniqueId}
                  bookmarks={bookmarks}
                  handleBookmarkChange={handleBookmarkChange}
                  bookmarkParams={bookmarkParams}
                />
              }
            </Box>
          </HStack>

          <Text pr={{ base: 4, md: 0 }} pl={{ base: 4, md: 0 }}>
            {baseData.Region}, {baseData.City}
          </Text>

          <Flex
            h={{ base: 'auto', xl: 500 }}
            flexDirection={{ base: 'column', xl: 'row' }}
            w={'100%'}
            alignItems={'stretch'}
            gridGap={4}>
            <VStack gridGap={0} flex={1}>
              <Box w={'100%'} h={{ base: 'unset', md: 530, xl: '100%' }}>
                <ImageGallery images={imagesData} showMore={true} />
              </Box>
            </VStack>
            <Flex
              w={{ base: '100%', xl: 370 }}
              alignItems={'flex-start'}
              pr={{ base: 4, md: 0 }}
              pl={{ base: 4, md: 0 }}>
              <VStack alignItems={'stretch'} width={'100%'} height={'100%'} gridGap={4}>
                {(price && !baseData.DisableOnlinePriceCalculation) && (
                  <Flex
                    flexDirection={{ base: 'column', md: 'row' }}
                    alignItems={'baseline'}
                    justify={'space-between'}>
                    <ObjectPriceInformation
                      prices={price}
                      bookingRouteParams={bookingRouteParams}
                    />
                  </Flex>
                )}
                <VStack alignItems={'stretch'}>
                  <Box width={'100%'}>
                    {isTabletOrMobile && !bookingRouteParams.fromDate && (
                      <Button
                        width={'100%'}
                        colorScheme={'blue'}
                        onClick={() => handleBookingCalendarModal(true)}>
                        Verfügbarkeit prüfen
                      </Button>
                    )}

                    {isTabletOrMobile && bookingRouteParams.fromDate && (
                      <ScrollLink
                        to="detailsBookingCalendar"
                        spy={true}
                        smooth={true}
                        duration={500}
                        offset={70}>
                        <Button width={'100%'} colorScheme={'blue'}>
                          {bookingButtonText}
                        </Button>
                      </ScrollLink>
                    )}

                    {!isTabletOrMobile && (
                      <ScrollLink
                        to="detailsBookingCalendar"
                        spy={true}
                        smooth={true}
                        duration={500}
                        offset={70}>
                        <Button width={'100%'} colorScheme={'blue'}>
                          {bookingRouteParams.fromDate
                            ? bookingButtonText
                            : 'Verfügbarkeit prüfen'}
                        </Button>
                      </ScrollLink>
                    )}
                    <Text fontSize={'sm'} color={'gray.400'} textAlign={'center'}>
                      Bitte wählen Sie einen Zeitraum um Preise anzuzeigen.
                    </Text>
                  </Box>
                </VStack>
                <ObjectHighlightedBaseData baseData={baseData} />
              </VStack>
            </Flex>
          </Flex>
        </VStack>
      )}
    </>
  );
};
